
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Chip, Stack, Avatar, Grid, Paper, CircularProgress } from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { commonRunQuery } from "../../util/API/loans";
import { PortfolioDownloadDocuments } from "../../util/API/portfolio";

const DocumentVault = ({ onViewAll, userRole }) => {
  const isAdmin = userRole === 'admin';
  const [activeTab, setActiveTab] = useState('All Docs');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = JSON.parse(sessionStorage.getItem("user"))?.userid;

  useEffect(() => {
    getDocuments();
  }, []);
const handleDownload = async (docId, docName) => {
  if (!docId) return;
  try {
    const response = await PortfolioDownloadDocuments(docId);

    const accessUrl = response?.data?.payload?.downloadUrl || response?.payload?.downloadUrl;

    if (!accessUrl) {
      console.error("No access URL returned");
      return;
    }

    const cleanUrl = accessUrl.replace(/&amp;/g, '&').replace(/\s+/g, '');
    window.open(cleanUrl, '_blank');

  } catch (error) {
    console.error("Download failed:", error);
  }
};
  const getDocuments = async () => {
    setLoading(true);
    try {
      const response = await commonRunQuery(371, userId);
      // console.log("Documents Response ????????????", JSON.stringify(response));

      const record = response?.data?.body?.record;
      if (record) {
        // Handle both single object and array
        const recordsArray = Array.isArray(record) ? record : [record];
        const mapped = recordsArray.map((item) => {
          const doc = item.documents;
          return {
             id: doc?.id, 
            name: doc?.file_name || "—",
            info: `${doc?.document_type?.toUpperCase() || "FILE"} • ${doc?.access_role || "—"} • ${doc?.owner_type || "—"}`,
            fileKey: doc?.file_key,
            accessRole: doc?.access_role,
            ownerType: doc?.owner_type,
            createdAt: doc?.created_at,
          };
        });
        setDocuments(mapped);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tabs based on access_role / owner_type
  const documentData = {
    'All Docs': documents,
    'My Private Docs': documents.filter((d) => d.accessRole === 'private'),
    'Fund-wide Docs': documents.filter((d) => d.ownerType === 'admin'),
    'Recent Docs': [...documents].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    ).slice(0, 5),
  };

  const adminRepos = [
    {
      id: 'global',
      title: 'Global Repository',
      description: 'Manage fund-wide prospectuses, compliance reports, and public templates.',
      icon: <LanguageIcon sx={{ color: '#1976d2' }} />,
      bgColor: '#e3f2fd',
    },
    {
      id: 'investor',
      title: 'Investors Repository',
      description: 'Access private subscription agreements, tax statements, and individual portfolios.',
      icon: <FolderSharedIcon sx={{ color: '#2e7d32' }} />,
      bgColor: '#e8f5e9',
    },
  ];

  const tabs = ['All Docs', 'My Private Docs', 'Fund-wide Docs', 'Recent Docs'];

  if (isAdmin) {
    return (
      <Box
        className="dashboard-card"
        sx={{ height: 350, display: "flex", flexDirection: "column", p: 3 }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, flexShrink: 0 }}>
          Document Management
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Grid container spacing={3}>
            {adminRepos.map((repo) => (
              <Grid item xs={12} md={6} key={repo.id}>
                <Paper
                  elevation={0}
                  onClick={() => onViewAll(repo.id)}
                  sx={{
                    p: 3,
                    border: '1px solid #eee',
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': {
                      borderColor: '#1976d2',
                      boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2.5}>
                    <Avatar sx={{ bgcolor: repo.bgColor, width: 56, height: 56 }}>
                      {repo.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#002e4c">
                        {repo.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, maxWidth: 220 }}>
                        {repo.description}
                      </Typography>
                    </Box>
                  </Box>
                  <ArrowForwardIosIcon sx={{ fontSize: 16, color: '#ccc' }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="dashboard-card"
      sx={{ height: 350, display: "flex", flexDirection: "column" }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ flexShrink: 0 }}>
        <Typography variant="h6">Document Vault</Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {documentData[activeTab].length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="160px">
              <Typography variant="body2" color="textSecondary">
                No documents found.
              </Typography>
            </Box>
          ) : (
            documentData[activeTab].map((doc, index) => (
              <Box
                key={index}
                sx={{ border: '1px solid #eee', p: 2, borderRadius: 2, mb: 1, position: 'relative' }}
              >
                <Typography variant="subtitle2">{doc.name}</Typography>
                <Typography variant="caption" color="textSecondary">{doc.info}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                   onClick={() => handleDownload(doc.id, doc.name)}
                  sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', textTransform: 'none' }}
                >
                  Download
                </Button>
              </Box>
            ))
          )}
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          variant="text"
          onClick={onViewAll}
          endIcon={<ArrowForwardIcon />}
          sx={{ textTransform: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', ml: 1 }}
        >
          View All Files
        </Button>
      </Stack>
    </Box>
  );
};

export default DocumentVault;
